// ** React Imports
import { useState, useEffect } from "react";

// ** Redux & Store & Actions
import { connect } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { useWizard } from "react-use-wizard";

// ** Custom Components
import ErrorMessage from "containers/Landing/components/ErrorMessage";
import ProgressBar from "../ProgressBar";
import NextButton from "../NextButton";
import PrevButton from "../PrevButton";
import StepLabel from "../StepLabel";
import CoupleGenderContent from "./CoupleGenderContent";

// ** Custom Hooks
import useValidateDate from "containers/Landing/containers/Register/hooks/useValidateDate";
import useCurrentStep from "../../hooks/useCurrentStep";

// ** Helpers
import { trackUserRegistrationStep2 } from "helpers/analytics";

// ** Styled Components
import {
	StyledContentWrapper,
	StyledButtonWrapper,
	StyledPrevButtonContainer,
	StyledNextButtonContainer,
} from "containers/Landing/containers/Register/styles";

// #####################################################

const StepCoupleGender = ({
	currentStep,
	totalSteps,
	selectedGenderCouple,
	selectedGenderCouplePartner,
	isDateSelectedPartner1,
	isDateSelectedPartner2,
	selectedDayPartner1,
	selectedMonthPartner1,
	selectedYearPartner1,
	selectedDayPartner2,
	selectedMonthPartner2,
	selectedYearPartner2,
	oAuth,
	setOAuth,
	setShowContinueWithOAuthButton,
}) => {
	const { t } = useTranslation("common");

	useCurrentStep();

	const { previousStep, nextStep, goToStep } = useWizard();

	const [showGenderEmptyMessagePartner1, setShowGenderEmptyMessagePartner1] =
		useState(false);
	const [showGenderEmptyMessagePartner2, setShowGenderEmptyMessagePartner2] =
		useState(false);
	const [showAgeEmptyMessagePartner1, setShowAgeEmptyMessagePartner1] =
		useState(false);
	const [showAgeEmptyMessagePartner2, setShowAgeEmptyMessagePartner2] =
		useState(false);

	const {
		dateError: dateErrorPartner1,
		dateErrorMessage: dateErrorMessagePartner1,
	} = useValidateDate({
		day: selectedDayPartner1,
		month: selectedMonthPartner1,
		year: selectedYearPartner1,
		isDateSelected: isDateSelectedPartner1,
	});

	const {
		dateError: dateErrorPartner2,
		dateErrorMessage: dateErrorMessagePartner2,
	} = useValidateDate({
		day: selectedDayPartner2,
		month: selectedMonthPartner2,
		year: selectedYearPartner2,
		isDateSelected: isDateSelectedPartner2,
	});

	const isStepReady = Boolean(
		selectedGenderCouple &&
			selectedGenderCouplePartner &&
			isDateSelectedPartner1 &&
			isDateSelectedPartner2 &&
			!dateErrorPartner1 &&
			!dateErrorPartner2
	);

	useEffect(() => {
		if (oAuth.reset) {
			goToStep(0);
			setOAuth({ ...oAuth, reset: false });
		}
		setShowContinueWithOAuthButton(false);
	}, [oAuth.reset]);

	// #####################################################

	return (
		<>
			<StyledContentWrapper>
				{/* Nagłówek */}
				<StepLabel
					label={t("landing:register.selectGenderAndAgeForCouple")}
				/>

				{/* Główna Zawartość Kroku */}
				<CoupleGenderContent
					dateErrorPartner1={dateErrorPartner1}
					dateErrorMessagePartner1={dateErrorMessagePartner1}
					showGenderEmptyMessagePartner1={
						showGenderEmptyMessagePartner1
					}
					setShowGenderEmptyMessagePartner1={
						setShowGenderEmptyMessagePartner1
					}
					setShowGenderEmptyMessagePartner2={
						setShowGenderEmptyMessagePartner2
					}
					showAgeEmptyMessagePartner1={showAgeEmptyMessagePartner1}
					setShowAgeEmptyMessagePartner1={
						setShowAgeEmptyMessagePartner1
					}
					setShowAgeEmptyMessagePartner2={
						setShowAgeEmptyMessagePartner2
					}
				/>
			</StyledContentWrapper>

			{/* Komunikaty Błędów */}
			{!dateErrorPartner2 && showGenderEmptyMessagePartner2 && (
				<ErrorMessage>
					{t("landing:register.selectGenderEmpty")}
				</ErrorMessage>
			)}
			{!dateErrorPartner2 && showAgeEmptyMessagePartner2 && (
				<ErrorMessage>{t("landing:register.dateEmpty")}</ErrorMessage>
			)}
			{dateErrorPartner2 && (
				<ErrorMessage>{dateErrorMessagePartner2}</ErrorMessage>
			)}

			{/* Pasek Postępu */}
			<ProgressBar currentStep={currentStep} totalSteps={totalSteps} />

			{/* Przyciski "Next" && "Prev" */}
			<StyledButtonWrapper flex>
				<StyledPrevButtonContainer>
					<PrevButton onClick={previousStep} />
				</StyledPrevButtonContainer>
				<StyledNextButtonContainer>
					<NextButton
						onClick={() => {
							if (isStepReady) {
								trackUserRegistrationStep2();
								nextStep();
							} else if (!selectedGenderCouple) {
								setShowGenderEmptyMessagePartner1(true);
							} else if (!isDateSelectedPartner1) {
								setShowAgeEmptyMessagePartner1(true);
							} else if (!selectedGenderCouplePartner) {
								setShowGenderEmptyMessagePartner2(true);
							} else if (!isDateSelectedPartner2) {
								setShowAgeEmptyMessagePartner2(true);
							}
						}}
					/>
				</StyledNextButtonContainer>
			</StyledButtonWrapper>
		</>
	);
};

// #####################################################

const mapStateToProps = ({
	auth: {
		register: {
			registerForm: {
				ageCoupleCase: {
					partner1: {
						day: selectedDayPartner1,
						month: selectedMonthPartner1,
						year: selectedYearPartner1,
					},
					partner2: {
						day: selectedDayPartner2,
						month: selectedMonthPartner2,
						year: selectedYearPartner2,
					},
					isSelectedPartner1,
					isSelectedPartner2,
				},
				genderCoupleCase: { partner1, partner2 },
			},
		},
	},
}) => ({
	selectedGenderCouple: partner1,
	selectedGenderCouplePartner: partner2,
	isDateSelectedPartner1: isSelectedPartner1,
	isDateSelectedPartner2: isSelectedPartner2,
	selectedDayPartner1,
	selectedMonthPartner1,
	selectedYearPartner1,
	selectedDayPartner2,
	selectedMonthPartner2,
	selectedYearPartner2,
});

export default connect(mapStateToProps, null)(StepCoupleGender);

// #####################################################
